import type { AABB } from '../types';
import { CITY_EXTENT, CHAR_RADIUS } from '../config';

export class CollisionWorld {
  private boxes: AABB[] = [];
  private boundsCount = 0; // Track how many are city bounds

  addAABB(aabb: AABB) {
    this.boxes.push(aabb);
  }

  // Clear building colliders (keep city bounds)
  clearBuildings() {
    if (this.boundsCount > 0) {
      this.boxes = this.boxes.slice(0, this.boundsCount);
    } else {
      this.boxes = [];
    }
  }

  addCityBounds() {
    const e = CITY_EXTENT;
    const wallThick = 0.5;
    const h = 20;
    // North wall
    this.boxes.push({ minX: -e - wallThick, maxX: e + wallThick, minZ: -e - wallThick, maxZ: -e, minY: 0, maxY: h });
    // South wall
    this.boxes.push({ minX: -e - wallThick, maxX: e + wallThick, minZ: e, maxZ: e + wallThick, minY: 0, maxY: h });
    // West wall
    this.boxes.push({ minX: -e - wallThick, maxX: -e, minZ: -e, maxZ: e, minY: 0, maxY: h });
    // East wall
    this.boxes.push({ minX: e, maxX: e + wallThick, minZ: -e, maxZ: e, minY: 0, maxY: h });
    this.boundsCount = this.boxes.length;
  }

  // Sphere vs AABB collision resolution
  resolveCollision(
    px: number,
    py: number,
    pz: number,
    radius: number
  ): { x: number; y: number; z: number; grounded: boolean } {
    let x = px;
    let y = py;
    let z = pz;
    let grounded = false;

    for (const box of this.boxes) {
      // Find closest point on AABB to sphere center
      const closestX = Math.max(box.minX, Math.min(x, box.maxX));
      const closestY = Math.max(box.minY, Math.min(y, box.maxY));
      const closestZ = Math.max(box.minZ, Math.min(z, box.maxZ));

      const dx = x - closestX;
      const dy = y - closestY;
      const dz = z - closestZ;
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < radius * radius) {
        const dist = Math.sqrt(distSq);
        if (dist === 0) {
          // Inside the box — push out along smallest axis
          const pushXMin = x - box.minX + radius;
          const pushXMax = box.maxX - x + radius;
          const pushZMin = z - box.minZ + radius;
          const pushZMax = box.maxZ - z + radius;
          const minPush = Math.min(pushXMin, pushXMax, pushZMin, pushZMax);

          if (minPush === pushXMin) x = box.minX - radius;
          else if (minPush === pushXMax) x = box.maxX + radius;
          else if (minPush === pushZMin) z = box.minZ - radius;
          else z = box.maxZ + radius;
        } else {
          // Push sphere out along penetration vector
          const overlap = radius - dist;
          const nx = dx / dist;
          const ny = dy / dist;
          const nz = dz / dist;

          x += nx * overlap;
          y += ny * overlap;
          z += nz * overlap;

          // If pushed up by a box top, we're grounded
          if (ny > 0.5) grounded = true;
        }
      }
    }

    return { x, y, z, grounded };
  }

  getBoxCount(): number {
    return this.boxes.length;
  }

  /**
   * Cast a ray and return the distance to the nearest AABB hit, or maxDist if
   * unobstructed. If the origin is inside an AABB, returns 0. Used by the
   * third-person camera to pull in when buildings would occlude the player.
   * Direction must be normalized.
   */
  raycast(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxDist: number,
  ): number {
    let closest = maxDist;
    for (const box of this.boxes) {
      if (
        ox >= box.minX && ox <= box.maxX &&
        oy >= box.minY && oy <= box.maxY &&
        oz >= box.minZ && oz <= box.maxZ
      ) {
        return 0;
      }

      let tmin = -Infinity;
      let tmax = Infinity;
      let outside = false;

      // Slab test, per axis. A near-zero direction component means the ray is
      // parallel to that pair of slabs — only a hit if origin sits between them.
      if (Math.abs(dx) > 1e-8) {
        const t1 = (box.minX - ox) / dx;
        const t2 = (box.maxX - ox) / dx;
        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
      } else if (ox < box.minX || ox > box.maxX) outside = true;

      if (!outside && Math.abs(dy) > 1e-8) {
        const t1 = (box.minY - oy) / dy;
        const t2 = (box.maxY - oy) / dy;
        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
      } else if (!outside && (oy < box.minY || oy > box.maxY)) outside = true;

      if (!outside && Math.abs(dz) > 1e-8) {
        const t1 = (box.minZ - oz) / dz;
        const t2 = (box.maxZ - oz) / dz;
        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
      } else if (!outside && (oz < box.minZ || oz > box.maxZ)) outside = true;

      if (outside) continue;
      if (tmax >= Math.max(tmin, 0) && tmin < closest && tmin >= 0) {
        closest = tmin;
      }
    }
    return closest;
  }
}
